/**
 * The two confirmation dialogs (piece 3, decision 0093). The destructive one
 * confirms by a typed phrase, never a second click, and names specifically
 * what is being deleted; the ordinary one states its consequence as content,
 * and a bare "Are you sure?" is the failure it exists to prevent. Button colour
 * alone chooses between them.
 */
export function destructivePhraseMatches(typed: string, phrase: string): boolean {
  return typed === phrase;
}

const BARE = /^\s*(are you sure\??|confirm\??|ok\??)?\s*$/i;

export function isBareConfirmation(body: string): boolean {
  return BARE.test(body);
}

export interface Consequence {
  body: string;
  /** Records the consequence touches, listed by name; a count with no names fails. */
  recordNames?: readonly string[];
}

const COUNTS_RECORDS = /\b\d+\b[^.]*\b(record|document|file|item|person|people|contact|invoice|order)s?\b/i;

export function assertConsequence(consequence: Consequence): void {
  if (isBareConfirmation(consequence.body)) {
    throw new Error('A Confirmation dialog names its consequence; "Are you sure?" alone is refused (piece 3)');
  }
  if (COUNTS_RECORDS.test(consequence.body) && (consequence.recordNames === undefined || consequence.recordNames.length === 0)) {
    throw new Error('A consequence that counts records lists them by name (piece 3)');
  }
}

export interface DestructiveSubject {
  /** What is being deleted, named specifically: the Deal "Acme roof replacement", never "this record". */
  subject: string;
  /** The phrase the person types to confirm. */
  phrase: string;
  /**
   * 🔒 Decision 12970: the delete goes to the Trash, where Restore brings it back, so the dialog says so and never
   * "permanently". Absent on every delete that is permanent.
   */
  toTrash?: boolean;
}

const GENERIC_SUBJECT = /^\s*(this|the|that)?\s*(record|item|entry|object)?\s*$/i;

export function assertSpecificSubject(subject: string): void {
  if (GENERIC_SUBJECT.test(subject)) {
    throw new Error('The Destructive Action Confirmation Dialog names the specific record being deleted (piece 3)');
  }
}
